import { SvgIcon } from "@progress/kendo-react-common";
import { AppBar, AppBarSection, Avatar, CardTitle } from "@progress/kendo-react-layout";
import { logoutIcon, userIcon } from "@progress/kendo-svg-icons";
import { useEffect } from "react";
import { useGetUserInfoMutation } from "../api/userApi";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import logoUrl from "../assets/logo-200.png";
import Notifications from "../components/notify/notifications";
import { AuthSessionGuard } from "../features/auth/authSessionGuard";
import { SignalRProvider } from "../features/signalr/signalRProvider";
import { AppStorage } from "../features/storage/appStorage";
import { logout, updateUserInfo, userSliceState } from "../slices/userSlice";
import { isAdministrator } from "../utils/userRoleUtils";
import { Bell, Settings, ShieldUser, CircleQuestionMark } from "lucide-react";
import ActionButton from "../pages/workspace/components/atoms/actionButton";
import ActionButtonMenu from "../pages/workspace/components/atoms/actionButtonMenu";

const menuItems: Array<{ path: string; label: string }> = [
    { path: "/app/home", label: "Home" },
    { path: "/app/workspace/simulator", label: "Simulator" },
    { path: "/app/workspace/scenarios", label: "Scenarios" }
];

const LoggedInLayout: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const isAuthenticated = AppStorage.isAuthenticated();
    const user = useAppSelector(userSliceState);
    const userEmail = user.email?.trim() || null;
    const fullName = [user.firstName?.trim(), user.lastName?.trim()].filter(Boolean).join(" ");
    const displayName = fullName || userEmail;
    const userInitial = displayName?.match(/[\p{L}\p{N}]/u)?.[0].toUpperCase() ?? null;
    const [getUserInfo] = useGetUserInfoMutation();

    useEffect(() => {
        if (isAuthenticated) {
            void getUserInfo().then(result => {
                if (result.data) {
                    dispatch(updateUserInfo(result.data));
                }
            });
        }
    }, [dispatch, getUserInfo, isAuthenticated]);
    const userIsAdministrator = isAdministrator();

    const handleLogout = () => {
        dispatch(logout());
        navigate("/");
    };

    return (
        <div className="d-flex flex-column vh-100 overflow-hidden">
            <Notifications />
            <AppBar className="flex-shrink-0 border border-secondary-subtle shadow-sm px-3 px-lg-4 py-3 bg-white align-items-center justify-content-between flex-wrap gap-2">
                <AppBarSection className="flex-grow-1 align-items-center gap-2 gap-lg-5 min-w-0 flex-wrap">
                    <CardTitle className="mb-0 flex-shrink-0">
                        <span className="d-inline-flex align-items-center gap-2">
                            <NavLink to="/app/home">
                                <img
                                    src={logoUrl}
                                    alt="Sovra"
                                    style={{
                                        height: "1.5rem",
                                        width: "auto",
                                        display: "block",
                                        objectFit: "contain"
                                    }}
                                />
                            </NavLink>
                        </span>
                    </CardTitle>

                    <ul className="nav nav-pills gap-2 mb-0 flex-wrap min-w-0">
                        {menuItems.map(item => (
                            <li key={item.path} className="nav-item">
                                <NavLink
                                    to={item.path}
                                    className={({ isActive }) => `nav-link rounded-2 px-3 py-2 ${isActive
                                        ? "active !k-bg-primary border-primary text-white"
                                        : "text-body"
                                    }`}
                                >
                                    {item.label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                </AppBarSection>

                {isAuthenticated && (
                    <AppBarSection style={{ marginLeft: "auto" }}>
                        <div className="d-flex align-items-center gap-2">
                            <ActionButton
                                variant="icon"
                                aria-label="Notifications"
                                title="Notifications"
                                endIcon={<Bell size={20} />}
                                onClick={() => {}}
                            />
                            <ActionButton
                                variant="icon"
                                aria-label="Help"
                                title="Help"
                                endIcon={<CircleQuestionMark size={20} />}
                                onClick={() => {}}
                            />
                            <ActionButton
                                variant="icon"
                                aria-label="Settings"
                                title="Settings"
                                endIcon={<Settings size={20} />}
                                onClick={() => navigate("/app/settings")}
                            />
                            {userIsAdministrator && (
                                <ActionButton
                                    variant="icon"
                                    aria-label="Admin"
                                    title="Admin"
                                    endIcon={<ShieldUser size={20} />}
                                    onClick={() => navigate("/app/admin")}
                                />
                            )}
                            <ActionButtonMenu>
                                <ActionButtonMenu.Trigger
                                    variant="icon"
                                    fillMode="solid"
                                    className="p-0 fw-semibold rounded-circle shadow-sm !k-text-white text-white-hover !k-bg-primary bg-action-primary-hover"
                                    style={{ width: "2.25rem", height: "2.25rem" }}
                                    title={displayName ?? "User menu"}
                                    aria-label={displayName ? `Toggle user menu for ${displayName}` : "Toggle user menu"}
                                >
                                    <span aria-hidden="true">
                                        {userInitial ?? <SvgIcon icon={userIcon} size="medium" />}
                                    </span>
                                </ActionButtonMenu.Trigger>
                                <ActionButtonMenu.Content>
                                    <div
                                        className="d-flex align-items-center gap-3 p-3 rounded-2 bg-body-tertiary"
                                        style={{ width: "min(20rem, calc(100vw - 4rem))" }}
                                    >
                                        <span className="flex-shrink-0" aria-hidden="true">
                                            <Avatar
                                                type={userInitial ? "text" : "icon"}
                                                rounded="full"
                                                themeColor="primary"
                                                fillMode="solid"
                                                className="fw-semibold"
                                                style={{ width: "2.75rem", height: "2.75rem" }}
                                            >
                                                {userInitial ?? <SvgIcon icon={userIcon} size="medium" />}
                                            </Avatar>
                                        </span>
                                        <div className="text-wrap text-break" style={{ minWidth: 0 }}>
                                            <div className="small text-body-secondary mb-1">Signed in as</div>
                                            <div className="fw-semibold">{displayName ?? "Account details unavailable"}</div>
                                        </div>
                                    </div>
                                    <div className="dropdown-divider my-2" role="separator" />
                                    <ActionButton
                                        variant="tertiary"
                                        innerClassName="w-100 d-flex justify-content-start gap-2 rounded-2"
                                        startIcon={<SvgIcon icon={logoutIcon} size="small" />}
                                        onClick={handleLogout}
                                    >
                                        Log out
                                    </ActionButton>
                                </ActionButtonMenu.Content>
                            </ActionButtonMenu>
                        </div>
                    </AppBarSection>
                )}
            </AppBar>
            <SignalRProvider autoConnect={isAuthenticated}>
                <div className="flex-grow-1 min-h-0 overflow-hidden d-flex flex-column">
                    <Outlet />
                </div>
            </SignalRProvider>
            <AuthSessionGuard />
        </div>
    );
};

export default LoggedInLayout;
